import { User } from '../types/auth';
import { setUser, clearUser } from './user';

// Token management
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';

// Store token in localStorage
export const setAuthToken = (token: string): void => {
  try {
    if (!token || typeof token !== 'string') {
      console.error('Invalid token provided to setAuthToken');
      return;
    }
    localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error('Error setting auth token:', error);
  }
};

// Get token from localStorage
export const getAuthToken = (): string | null => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      return null;
    }
    // Basic JWT validation
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT token format');
      return null;
    }
    return token;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

// Remove token from localStorage
export const removeAuthToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    console.error('Error removing auth token:', error);
  }
};

// Store refresh token in localStorage
export const setRefreshToken = (refreshToken: string): void => {
  try {
    if (!refreshToken || typeof refreshToken !== 'string') {
      console.error('Invalid refresh token provided to setRefreshToken');
      return;
    }
    console.log('[AUTH] Setting refresh token in localStorage');
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  } catch (error) {
    console.error('Error setting refresh token:', error);
  }
};

// Get refresh token from localStorage
export const getRefreshToken = (): string | null => {
  try {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      console.warn('[AUTH] No refresh token found in localStorage');
      return null;
    }
    console.log('[AUTH] Retrieved refresh token from localStorage');
    return refreshToken;
  } catch (error) {
    console.error('Error getting refresh token:', error);
    return null;
  }
};

// Remove refresh token from localStorage
export const removeRefreshToken = (): void => {
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Error removing refresh token:', error);
  }
};

// Store user data in localStorage
export const setUserData = (user: User | null): void => {
  setUser(user);
};

// Get user data from localStorage
export const getUserData = (): User | null => {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

// Remove user data from localStorage
export const removeUserData = (): void => {
  clearUser();
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  try {
    const token = getAuthToken();
    if (!token) return false;
    
    // Check if token is expired
    const payload = JSON.parse(atob(token.split('.')[1]));
    const isExpired = payload.exp * 1000 < Date.now();
    
    if (isExpired) {
      console.log('[auth] Token is expired');
      clearAuthData();
      return false;
    }
    
    return true;
  } catch (e) {
    console.error('[auth] Error checking authentication:', e);
    clearAuthData();
    return false;
  }
};

// Clear all auth data
export const clearAuthData = (): void => {
  removeAuthToken();
  removeRefreshToken();
  removeUserData();
  localStorage.removeItem('lastActivity');
  localStorage.removeItem('rememberMe');
};

// Get the authentication headers
export const getAuthHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Check if the token is expired
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch (e) {
    return true;
  }
};
